/**
 * Auth Service Factory
 *
 * Returns the Okta authentication service.
 *
 * This factory pattern allows for future authentication provider flexibility
 * without changing application code.
 *
 * Note: Microsoft Entra support was removed. If needed in the future, see the
 * archive/entra-implementation branch for the previous implementation.
 */

import { REACT_APP_DEV_AUTH_BYPASS } from '@env';

import { devAuthService } from './devAuthService';
// Okta service - platform-specific files (.web.ts / .native.ts)
// Webpack/Metro will automatically resolve the correct one
import { oktaAuthService } from './oktaAuthService';
import { IAuthService } from '../../types/auth';

type AuthProvider = 'okta' | 'dev';

/**
 * Is the development sign-in bypass on? (#1642)
 *
 * ⚠️ TWO CONDITIONS HERE AND A THIRD ON THE SERVER. `__DEV__` keeps it out of
 * every release bundle; the variable must be exactly `true`, so an empty or
 * missing value is off. Neither of those grants a session — only the API
 * running with `ENABLE_AUTH=false` does, and a deployed one never is. See
 * `devAuthService.ts` for why the bypass exists at all.
 */
function isDevAuthBypassEnabled(): boolean {
  return __DEV__ && String(REACT_APP_DEV_AUTH_BYPASS ?? '').trim() === 'true';
}

let cachedService: IAuthService | null = null;

/**
 * Get the configured auth provider from environment
 */
function getAuthProvider(): AuthProvider {
  if (isDevAuthBypassEnabled()) {
    return 'dev';
  }
  // Always use Okta authentication
  return 'okta';
}

/**
 * Get the appropriate authentication service based on configuration
 *
 * @returns {IAuthService} The configured authentication service
 *
 * @example
 * ```typescript
 * // In your component or context
 * const authService = getAuthService();
 * await authService.login();
 * ```
 */
export function getAuthService(): IAuthService {
  // Return cached service if available (singleton pattern)
  if (cachedService) {
    return cachedService;
  }

  const provider = getAuthProvider();

  if (provider === 'dev') {
    console.warn(
      '🔓 DEV AUTH BYPASS ACTIVE — signing in as whoever the API returns. ' +
        'Never enable REACT_APP_DEV_AUTH_BYPASS outside local development.',
    );
    cachedService = devAuthService;
    return devAuthService;
  }

  // Using Okta authentication service (platform-specific)

  // Cache the service instance
  cachedService = oktaAuthService;

  return oktaAuthService;
}

/**
 * Get the currently configured auth provider name
 * Useful for debugging or displaying provider info to users
 */
export function getCurrentProvider(): AuthProvider {
  return getAuthProvider();
}

/**
 * Reset the cached service (useful for testing)
 * @internal
 */
export function resetAuthServiceCache(): void {
  cachedService = null;
}

// Export for testing/debugging
export const _getAuthProvider = getAuthProvider;
